import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  PerpDex,
  MetaResponse,
  ClearinghouseState,
  FundingUpdate,
  NonFundingUpdate,
  FundingHistoryItem,
  PredictedFunding,
  PerpDeployAuctionStatus,
  ActiveAssetData,
  PerpDexLimits,
  InfoRequest,
  InfoResponse,
  AssetContext,
  UniverseItem,
  Candle,
  CandleInterval,
  SpotMeta,
  SpotAssetContext,
  SpotClearinghouseState,
  SpotDeployResponse,
  SpotPairDeployAuctionStatus,
  TokenDetails,
  UserPortfolio,
  UserReferral,
  UserFees,
  Delegation,
  DelegatorSummary,
  DelegatorHistory,
  DelegatorReward,
  OpenOrder,
} from '../types/hyperliquid';

export class HyperliquidInfo {
  private readonly client: AxiosInstance;
  private readonly baseURL: string;

  constructor(isMainnet: boolean = false) {
    this.baseURL = process.env.NEXT_PUBLIC_HYPERLIQUID_API_ENDPOINT;

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use(
      config => {
        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );

    this.client.interceptors.response.use(
      response => {
        return response;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      },
    );
  }

  private async postInfo<T extends InfoRequest['type']>(
    request: InfoRequest,
    retries: number = 3,
  ): Promise<InfoResponse<T>> {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await this.client.post('/info', request);
        return response.data;
      } catch (error: any) {
        const isRetryableError =
          error.response?.status === 502 ||
          error.response?.status === 503 ||
          error.response?.status === 504 ||
          error.code === 'ECONNRESET' ||
          error.code === 'ETIMEDOUT';

        if (isRetryableError && attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, attempt * 1000));
          continue;
        }

        throw error;
      }
    }
    throw new Error(`Failed after ${retries} attempts`);
  }

  async getAllPerpDexs(): Promise<PerpDex[]> {
    const response = await this.postInfo<'perpDexs'>({ type: 'perpDexs' });
    const [, ...dexs] = response;
    return dexs;
  }

  async getMeta(dex?: string): Promise<MetaResponse> {
    return await this.postInfo<'meta'>({ type: 'meta', dex });
  }

  async getMetaAndAssetCtxs(): Promise<{ universe: UniverseItem[]; assetCtxs: AssetContext[] }> {
    const response = await this.postInfo<'metaAndAssetCtxs'>({ type: 'metaAndAssetCtxs' });
    const [metaResponse, assetCtxs] = response;
    return {
      universe: metaResponse.universe,
      assetCtxs,
    };
  }

  async getUserClearinghouseState(user: string, dex?: string): Promise<ClearinghouseState> {
    return await this.postInfo<'clearinghouseState'>({
      type: 'clearinghouseState',
      user,
      dex,
    });
  }

  // Alias for consistency with spot naming
  async getPerpClearinghouseState(user: string, dex?: string): Promise<ClearinghouseState> {
    return this.getUserClearinghouseState(user, dex);
  }

  async getUserFunding(user: string, startTime: number, endTime?: number): Promise<FundingUpdate[]> {
    return await this.postInfo<'userFunding'>({
      type: 'userFunding',
      user,
      startTime,
      endTime,
    });
  }

  async getUserNonFundingLedgerUpdates(user: string, startTime: number, endTime?: number): Promise<NonFundingUpdate[]> {
    return await this.postInfo<'userNonFundingLedgerUpdates'>({
      type: 'userNonFundingLedgerUpdates',
      user,
      startTime,
      endTime,
    });
  }

  async getFundingHistory(coin: string, startTime: number, endTime?: number): Promise<FundingHistoryItem[]> {
    return await this.postInfo<'fundingHistory'>({
      type: 'fundingHistory',
      coin,
      startTime,
      endTime,
    });
  }

  async getPredictedFundings(): Promise<[string, [string, PredictedFunding][]][]> {
    return await this.postInfo<'predictedFundings'>({ type: 'predictedFundings' });
  }

  async getPerpsAtOpenInterestCap(): Promise<string[]> {
    return await this.postInfo<'perpsAtOpenInterestCap'>({ type: 'perpsAtOpenInterestCap' });
  }

  async getPerpDeployAuctionStatus(): Promise<PerpDeployAuctionStatus> {
    return await this.postInfo<'perpDeployAuctionStatus'>({ type: 'perpDeployAuctionStatus' });
  }

  async getActiveAssetData(user: string, coin: string): Promise<ActiveAssetData> {
    return await this.postInfo<'activeAssetData'>({
      type: 'activeAssetData',
      user,
      coin,
    });
  }

  async getPerpDexLimits(dex: string): Promise<PerpDexLimits> {
    return await this.postInfo<'perpDexLimits'>({
      type: 'perpDexLimits',
      dex,
    });
  }

  /**
   * Get asset prices for all assets
   */
  async getAssetPrices(): Promise<Map<string, number>> {
    try {
      const { assetCtxs, universe } = await this.getMetaAndAssetCtxs();
      const prices = new Map<string, number>();

      universe.forEach((asset, index) => {
        if (assetCtxs[index]) {
          prices.set(asset.name, parseFloat(assetCtxs[index].markPx));
        }
      });

      return prices;
    } catch (error) {
      throw error;
    }
  }

  async getOpenInterest(): Promise<Map<string, number>> {
    try {
      const { assetCtxs, universe } = await this.getMetaAndAssetCtxs();
      const openInterest = new Map<string, number>();

      universe.forEach((asset, index) => {
        if (assetCtxs[index]) {
          openInterest.set(asset.name, parseFloat(assetCtxs[index].openInterest));
        }
      });

      return openInterest;
    } catch (error) {
      throw error;
    }
  }

  async getFundingRates(): Promise<Map<string, number>> {
    try {
      const { assetCtxs, universe } = await this.getMetaAndAssetCtxs();
      const fundingRates = new Map<string, number>();

      universe.forEach((asset, index) => {
        if (assetCtxs[index]) {
          fundingRates.set(asset.name, parseFloat(assetCtxs[index].funding));
        }
      });

      return fundingRates;
    } catch (error) {
      throw error;
    }
  }

  async getVolume24h(): Promise<Map<string, number>> {
    try {
      const { assetCtxs, universe } = await this.getMetaAndAssetCtxs();
      const volumes = new Map<string, number>();

      universe.forEach((asset, index) => {
        if (assetCtxs[index]) {
          volumes.set(asset.name, parseFloat(assetCtxs[index].dayNtlVlm));
        }
      });

      return volumes;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get candle snapshot data for a specific coin and time range
   * @param coin - The coin symbol (e.g., "BTC")
   * @param interval - Time interval (1m, 3m, 5m, 15m, 30m, 1h, 2h, 4h, 8h, 12h, 1d, 3d, 1w, 1M)
   * @param startTime - Start time in epoch milliseconds
   * @param endTime - End time in epoch milliseconds
   * @returns Array of candle data (max 5000 candles)
   */
  async getCandleSnapshot(
    coin: string,
    interval: CandleInterval,
    startTime: number,
    endTime: number,
  ): Promise<Candle[]> {
    try {
      const response = await this.client.post('/info', {
        type: 'candleSnapshot',
        req: {
          coin,
          interval,
          startTime,
          endTime,
        },
      });
      return response.data as Candle[];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all mid prices
   */
  async getAllMids(): Promise<Record<string, string>> {
    try {
      const response = await this.postInfo({ type: 'allMids' });
      return response as Record<string, string>;
    } catch (error) {
      throw error;
    }
  }

  async getL2Book(coin: string): Promise<any> {
    try {
      const response = await this.postInfo({
        type: 'l2Book',
        coin,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get spot metadata including tokens and trading pairs
   */
  async getSpotMeta(): Promise<SpotMeta> {
    try {
      const response = await this.postInfo<'spotMeta'>({ type: 'spotMeta' });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get spot metadata and asset contexts
   */
  async getSpotMetaAndAssetCtxs(): Promise<[SpotMeta, SpotAssetContext[]]> {
    try {
      const response = await this.postInfo<'spotMetaAndAssetCtxs'>({ type: 'spotMetaAndAssetCtxs' });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's spot token balances
   */
  async getUserSpotClearinghouseState(user: string): Promise<SpotClearinghouseState> {
    try {
      const response = await this.postInfo<'spotClearinghouseState'>({
        type: 'spotClearinghouseState',
        user,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Alias for backward compatibility
  async getSpotClearinghouseState(user: string): Promise<SpotClearinghouseState> {
    return this.getUserSpotClearinghouseState(user);
  }

  /**
   * Get information about the Spot Deploy Auction
   */
  async getSpotDeployState(user: string): Promise<SpotDeployResponse> {
    try {
      const response = await this.postInfo<'spotDeployState'>({
        type: 'spotDeployState',
        user,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get information about the Spot Pair Deploy Auction
   */
  async getSpotPairDeployAuctionStatus(): Promise<SpotPairDeployAuctionStatus> {
    try {
      const response = await this.postInfo<'spotPairDeployAuctionStatus'>({
        type: 'spotPairDeployAuctionStatus',
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get detailed information about a token
   */
  async getTokenDetails(tokenId: string): Promise<TokenDetails> {
    try {
      const response = await this.postInfo<'tokenDetails'>({
        type: 'tokenDetails',
        tokenId,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's open orders
   */
  async getUserOpenOrders(user: string, dex?: string): Promise<OpenOrder[]> {
    try {
      const response = await this.postInfo({
        type: 'openOrders',
        user,
        dex,
      });
      return response as OpenOrder[];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's fills (trades)
   */
  async getUserFills(user: string, aggregateByTime?: boolean): Promise<any[]> {
    try {
      const response = await this.postInfo({
        type: 'userFills',
        user,
        aggregateByTime,
      });
      return response as any[];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's fills by time range
   */
  async getUserFillsByTime(
    user: string,
    startTime: number,
    endTime?: number,
    aggregateByTime?: boolean,
  ): Promise<any[]> {
    try {
      const response = await this.postInfo({
        type: 'userFillsByTime',
        user,
        startTime,
        endTime,
        aggregateByTime,
      });
      return response as any[];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's historical orders
   */
  async getHistoricalOrders(user: string): Promise<any[]> {
    try {
      const response = await this.postInfo({
        type: 'historicalOrders',
        user,
      });
      return response as any[];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's portfolio data including account value history and PnL
   */
  async getUserPortfolio(user: string): Promise<Array<[string, UserPortfolio]>> {
    try {
      const response = await this.postInfo<'portfolio'>({
        type: 'portfolio',
        user,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's referral information
   */
  async getUserReferral(user: string): Promise<UserReferral> {
    try {
      const response = await this.postInfo<'referral'>({
        type: 'referral',
        user,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's fee schedule and trading fees
   */
  async getUserFees(user: string): Promise<UserFees> {
    try {
      const response = await this.postInfo<'userFees'>({
        type: 'userFees',
        user,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's staking delegations
   */
  async getUserDelegations(user: string): Promise<Delegation[]> {
    try {
      const response = await this.postInfo<'delegations'>({
        type: 'delegations',
        user,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's staking summary
   */
  async getDelegatorSummary(user: string): Promise<DelegatorSummary> {
    try {
      const response = await this.postInfo<'delegatorSummary'>({
        type: 'delegatorSummary',
        user,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's staking history
   */
  async getDelegatorHistory(user: string): Promise<DelegatorHistory[]> {
    try {
      const response = await this.postInfo<'delegatorHistory'>({
        type: 'delegatorHistory',
        user,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's staking rewards
   */
  async getDelegatorRewards(user: string): Promise<DelegatorReward[]> {
    try {
      const response = await this.postInfo<'delegatorRewards'>({
        type: 'delegatorRewards',
        user,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get maximum builder fee for a given address
   * @param user - The user address to get builder fee for
   * @returns Builder fee information including accumulated fees and stats
   */
  async getMaxBuilderFee(user: string, builder: string): Promise<number> {
    try {
      const response = await this.postInfo({
        type: 'maxBuilderFee',
        user,
        builder: builder,
      });
      return response as number;
    } catch (error) {
      throw error;
    }
  }
}
